import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";
import { Contract } from "@coti-io/coti-ethers";
import { z } from "zod";
import { buildStringInputText } from "@coti-io/coti-sdk-typescript";

// Schema for minting parameters
export const MintPrivateERC721Schema = z.object({
    tokenAddress: z.string().describe("ERC721 token contract address"),
    toAddress: z.string().describe("Address to receive the minted NFT"),
    tokenUri: z.string().describe("URI for the token metadata"),
    gasLimit: z.number().optional().describe("Optional gas limit"),
});

export type MintPrivateERC721Params = z.infer<typeof MintPrivateERC721Schema>;

// COTI Private ERC721 ABI for minting (requires encrypted token URI)
const ERC721_ABI = [
    // COTI private mint function
    {
        inputs: [
            { internalType: "address", name: "to", type: "address" },
            {
                components: [
                    {
                        components: [
                            { internalType: "ctUint64[]", name: "value", type: "uint256[]" }
                        ],
                        internalType: "struct ctString",
                        name: "ciphertext",
                        type: "tuple"
                    },
                    { internalType: "bytes[]", name: "signature", type: "bytes[]" }
                ],
                internalType: "struct itString",
                name: "itTokenURI",
                type: "tuple"
            }
        ],
        name: "mint",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function"
    },
    // Standard view functions
    "function tokenURI(uint256 tokenId) view returns (tuple(uint256[]))",
    "function ownerOf(uint256 tokenId) view returns (address)",
    "function totalSupply() view returns (uint256)"
];

export const mintPrivateErc721TokenAction: Action = {
    name: "MINT_PRIVATE_ERC721_TOKEN",
    similes: [
        "MINT_ERC721",
        "MINT_NFT",
        "CREATE_NFT",
        "MINT_PRIVATE_NFT",
        "ISSUE_NFT",
        "CREATE_TOKEN",
        "MINT_TOKEN",
        "MINT_PRIVATE_ERC721_TOKEN",
        "MINT_PRIVATE_ERC721",
        "CREATE_PRIVATE_NFT"
    ],
    description: "Mint a new private ERC721 NFT token on the COTI blockchain. This creates a new NFT with the specified metadata URI and assigns it to the recipient address.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`🔍 MINT_PRIVATE_ERC721_TOKEN validation called!`);
            elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - text: "${text}"`);
            
            // Check for minting keywords
            const isMintQuery = /\b(mint|create).*\b(nft|token)\b/i.test(text);
            elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - isMintQuery: ${isMintQuery}`);
            
            // Check if message content has required fields
            if (message.content?.tokenAddress && message.content?.toAddress && message.content?.tokenUri) {
                elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - structured params found`);
                return true;
            }

            // Check if text contains addresses and URI
            const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
            const uriMatch = text.match(/https?:\/\/[^\s]+/i);
            
            elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - addresses found: ${addressMatches?.length || 0}`);
            elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - URI found: ${!!uriMatch}`);

            if (isMintQuery && addressMatches && addressMatches.length >= 2 && uriMatch) {
                elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - returning true`);
                return true;
            }

            elizaLogger.info(`MINT_PRIVATE_ERC721_TOKEN validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`MINT_PRIVATE_ERC721_TOKEN validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("🚀 MINT_PRIVATE_ERC721_TOKEN handler called!");
            elizaLogger.info(`Message content: ${JSON.stringify(message.content, null, 2)}`);

            let params: MintPrivateERC721Params;

            // Try to get parameters from message content
            if (message.content?.tokenAddress && message.content?.toAddress && message.content?.tokenUri) {
                params = MintPrivateERC721Schema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
                const uriMatch = text.match(/https?:\/\/[^\s]+/i);
                
                if (!addressMatches || addressMatches.length < 2 || !uriMatch) {
                    throw new Error("Could not extract required parameters from message");
                }
                
                // First address is typically the contract, second is the recipient
                params = {
                    tokenAddress: addressMatches[0],
                    toAddress: addressMatches[1],
                    tokenUri: uriMatch[0]
                };
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const wallet = walletProvider.getWallet();

            // Create COTI contract instance
            const contract = new Contract(
                params.tokenAddress,
                ERC721_ABI,
                wallet
            );

            elizaLogger.info(`Minting NFT to ${params.toAddress} with URI: ${params.tokenUri}`);

            // Get the mint function selector for encryption
            const mintSelector = contract.mint.fragment.selector;
            elizaLogger.info(`Mint function selector: ${mintSelector}`);

            // Get AES key from wallet provider for encryption
            const aesKey = walletProvider.getAesKey();
            if (!aesKey) {
                throw new Error("AES key not found in wallet provider. Cannot encrypt token URI for private NFT minting.");
            }

            // Encrypt the token URI using COTI SDK
            elizaLogger.info("Encrypting token URI for private NFT minting...");
            const encryptedInputText = buildStringInputText(
                params.tokenUri,
                { wallet: wallet, userKey: aesKey },
                params.tokenAddress,
                mintSelector
            );

            elizaLogger.info("Token URI encrypted successfully. Proceeding with mint transaction...");

            // Mint the token with encrypted URI
            const tx = await contract.mint(
                params.toAddress,
                encryptedInputText,
                {
                    gasLimit: params.gasLimit || 500000
                }
            );

            const receipt = await tx.wait();
            
            // Try to get the token ID from the transaction logs
            let tokenId = "N/A";
            if (receipt.logs && receipt.logs.length > 0) {
                // Look for Transfer event to get token ID
                try {
                    const transferEvent = receipt.logs.find((log: any) => 
                        log.topics && log.topics[0] === "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef"
                    );
                    if (transferEvent && transferEvent.topics && transferEvent.topics[3]) {
                        tokenId = parseInt(transferEvent.topics[3], 16).toString();
                    }
                } catch (e) {
                    elizaLogger.warn("Could not extract token ID from logs:", e);
                }
            }

            const responseText = `Successfully minted private ERC721 NFT!
Token ID: ${tokenId}
Contract: ${params.tokenAddress}
Recipient: ${params.toAddress}
Metadata URI: ${params.tokenUri}
Transaction Hash: ${tx.hash}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        tokenId,
                        tokenAddress: params.tokenAddress,
                        toAddress: params.toAddress,
                        tokenUri: params.tokenUri,
                        transactionHash: tx.hash,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    tokenId,
                    tokenAddress: params.tokenAddress,
                    toAddress: params.toAddress,
                    tokenUri: params.tokenUri,
                    transactionHash: tx.hash,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in MINT_PRIVATE_ERC721_TOKEN action:", error);
            const errorMessage = `Failed to mint private ERC721 token: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Mint an NFT to alice with metadata https://example.com/art/1.json",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll mint a private NFT with encrypted metadata for you.",
                    action: "MINT_PRIVATE_ERC721_TOKEN",
                },
            },
        ],
    ],
};
